/**
 * Rolled Steel Tee Bar 3D Mesh Component
 * 
 * Creates a fully solid 3D visualization of rolled steel tee bar sections (ISNT)
 * T-shaped profile with flange at top and web extending downward
 * 
 * Reference: ROLLED STEEL TEE BARS dimensions and properties.csv
 * 
 * Cross-section geometry (T-shape):
 * - Horizontal flange at top (full width)
 * - Vertical web extending downward from center of flange
 * - Fully solid construction - no hollow sections
 * - Sharp T-shape with clean edges
 * - Symmetric section about vertical axis
 * 
 * IMPORTANT: Following same pattern as other sections:
 * 1. Draw T-shape in XY plane
 * 2. Extrude along Z for beam length
 * 3. Rotate -90° around X-axis to convert to Y-extrusion
 * 4. Result: Extrusion along Y-axis (matches other sections)
 */

import React from 'react';
import { RolledSteelTeeBarSection } from '../../types/beamSection';
import * as THREE from 'three';

interface RolledSteelTeeBarMeshProps {
  section: RolledSteelTeeBarSection;
  color?: string;
}

export const RolledSteelTeeBarMesh: React.FC<RolledSteelTeeBarMeshProps> = ({ 
  section, 
  color = '#FF1493' // Deep Pink for tee bars
}) => {
  // Create the tee bar cross-section geometry
  const geometry = React.useMemo(() => {
    const { h, b, tf, tw, length } = section;
    
    // Scale factor: mm to scene units (match other angle sections for consistency)
    const scale = 0.09;
    
    // Convert dimensions to scene units
    const totalDepth = h * scale;           // Overall depth (web + flange)
    const flangeWidth = b * scale;          // Flange width
    const flangeThickness = tf * scale;     // Flange thickness
    const webThickness = tw * scale;        // Web thickness
    const webHeight = totalDepth - flangeThickness;  // Height of web below flange
    
    /**
     * Create T-shaped cross-section - FULLY SOLID
     * 
     * Simple T-shape with sharp edges:
     * - Top flange: horizontal rectangle (width b, thickness tf)
     * - Web: vertical rectangle (thickness tw, height h-tf)
     * - No hollow sections - completely filled
     * - Origin at center bottom of web
     * 
     * Drawing from bottom-left, counter-clockwise:
     */
    const shape = new THREE.Shape();
    
    // Half dimensions for symmetric construction
    const halfFlangeWidth = flangeWidth / 2;
    const halfWebThickness = webThickness / 2;
    
    // Start at bottom-left corner of web
    shape.moveTo(-halfWebThickness, 0);
    
    // 1. Left edge of web - go up to flange junction
    shape.lineTo(-halfWebThickness, webHeight);
    
    // 2. Transition from web to flange (left side)
    shape.lineTo(-halfFlangeWidth, webHeight);
    
    // 3. Left outer edge of flange - go up to top
    shape.lineTo(-halfFlangeWidth, totalDepth);
    
    // 4. Top edge of flange - go right across full width
    shape.lineTo(halfFlangeWidth, totalDepth);
    
    // 5. Right outer edge of flange - go down to junction
    shape.lineTo(halfFlangeWidth, webHeight);
    
    // 6. Transition from flange to web (right side)
    shape.lineTo(halfWebThickness, webHeight);
    
    // 7. Right edge of web - go down to bottom
    shape.lineTo(halfWebThickness, 0);
    
    // 8. Bottom edge of web - close the shape
    shape.lineTo(-halfWebThickness, 0);
    
    // Extrude the T-shape along Z-axis to create 3D solid
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: length,        // Extrudes along Z from 0 to +length (full beam length)
      bevelEnabled: false,  // No beveling - sharp edges
      steps: 1,             // Single step for solid extrusion
      curveSegments: 1      // Straight edges - no curves
    };
    
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
    // NOTE: Scale is already applied to cross-section dimensions above
    // Do NOT scale the geometry again, especially not the length!
    // The length should match the full beam length
    
    // Rotate -90° around X-axis: converts Z-extrusion to Y-extrusion
    // This makes the section run along the beam (Y-axis)
    geometry.rotateX(-Math.PI / 2);
    
    // Center along Y-axis (same as other sections)
    geometry.translate(0, -length / 2, 0);
    
    // Compute face normals for proper lighting
    geometry.computeVertexNormals();
    
    return geometry;
  }, [section]);

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial 
        color={color} 
        metalness={0.7}       // Slightly more metallic for steel appearance
        roughness={0.3}       // Smoother surface
        side={THREE.FrontSide} // Only front faces (solid object)
      />
    </mesh>
  );
};
